import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["trigger", "triggerLabel", "hidden", "listbox"]
  static values = {
    options: Array // [{ value, label, description }]
  }

  connect() {
    this.isOpen = false
    this.highlightedIndex = -1
    this.selectedValue = this.hiddenTarget.value || this.optionsValue[0]?.value || ""

    this._handleClickOutside = (event) => {
      if (!this.element.contains(event.target)) {
        this.close()
      }
    }
    document.addEventListener("click", this._handleClickOutside)

    this.renderTrigger()
  }

  disconnect() {
    document.removeEventListener("click", this._handleClickOutside)
  }

  toggle() {
    this.isOpen ? this.close() : this.open()
  }

  open() {
    if (this.isOpen) return
    this.isOpen = true
    this.renderList()
    this.positionListbox()
    this.listboxTarget.classList.add("is-open")
    this.triggerTarget.setAttribute("aria-expanded", "true")
  }

  positionListbox() {
    const rect = this.triggerTarget.getBoundingClientRect()
    const footer = document.querySelector(".editor-footer")
    const footerHeight = footer ? footer.offsetHeight : 0
    const spaceBelow = window.innerHeight - rect.bottom - footerHeight
    const listboxHeight = 208 // max-height(200) + padding(8)
    this.listboxTarget.classList.toggle("is-above", spaceBelow < listboxHeight)
  }

  close() {
    if (!this.isOpen) return
    this.isOpen = false
    this.listboxTarget.classList.remove("is-open")
    this.triggerTarget.setAttribute("aria-expanded", "false")
    this.highlightedIndex = -1
  }

  select(event) {
    const value = event.currentTarget.dataset.value
    this.selectedValue = value
    this.hiddenTarget.value = value
    this.renderTrigger()
    this.close()
    this.hiddenTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  handleKeydown(event) {
    if (!this.isOpen && (event.key === "ArrowDown" || event.key === "ArrowUp" || event.key === "Enter" || event.key === " ")) {
      event.preventDefault()
      this.open()
      return
    }

    if (!this.isOpen) return

    const count = this.optionsValue.length
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.highlightedIndex = Math.min(this.highlightedIndex + 1, count - 1)
        this.updateHighlight()
        break
      case "ArrowUp":
        event.preventDefault()
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0)
        this.updateHighlight()
        break
      case "Enter":
      case " ":
        event.preventDefault()
        if (this.highlightedIndex >= 0 && this.highlightedIndex < count) {
          const opt = this.optionsValue[this.highlightedIndex]
          this.selectedValue = opt.value
          this.hiddenTarget.value = opt.value
          this.renderTrigger()
          this.close()
          this.hiddenTarget.dispatchEvent(new Event("input", { bubbles: true }))
          this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))
        }
        break
      case "Escape":
        event.preventDefault()
        this.close()
        break
    }
  }

  renderTrigger() {
    const opt = this.optionsValue.find(o => o.value === this.selectedValue)
    if (opt && this.hasTriggerLabelTarget) {
      this.triggerLabelTarget.textContent = opt.label
    }
  }

  renderList() {
    const listbox = this.listboxTarget
    listbox.innerHTML = ""

    for (const opt of this.optionsValue) {
      const li = document.createElement("li")
      li.className = "vselect-option"
      if (opt.value === this.selectedValue) {
        li.classList.add("is-selected")
      }
      li.setAttribute("role", "option")
      li.dataset.value = opt.value

      const label = document.createElement("span")
      label.className = "vselect-option-label"
      label.textContent = opt.label

      const desc = document.createElement("span")
      desc.className = "vselect-option-desc"
      desc.textContent = opt.description

      li.appendChild(label)
      li.appendChild(desc)
      li.addEventListener("click", (e) => this.select(e))
      listbox.appendChild(li)
    }
  }

  updateHighlight() {
    const items = this.listboxTarget.querySelectorAll("[role='option']")
    items.forEach((item, i) => {
      item.classList.toggle("is-highlighted", i === this.highlightedIndex)
      if (i === this.highlightedIndex) {
        item.scrollIntoView({ block: "nearest" })
      }
    })
  }
}
